import { type ReactNode, useCallback, useState } from "react";
import { createPortal } from "react-dom";

/**
 * The charts' hover layer: one floating card per chart, beside the pointer
 * (or under a focused mark, for the keyboard). Fixed-positioned in a portal,
 * so a chart's `overflow-x-auto` never clips it; kept inside the viewport.
 * Decoration only — every value it shows is also in the chart's table view.
 */

export interface TipState {
	x: number;
	y: number;
	content: ReactNode;
}

export interface Tip {
	state: TipState | null;
	/** Pointer move / enter over a mark. */
	at: (e: { clientX: number; clientY: number }, content: ReactNode) => void;
	/** Keyboard focus on a mark: under its box. */
	under: (el: Element, content: ReactNode) => void;
	hide: () => void;
}

export function useTip(): Tip {
	const [state, setState] = useState<TipState | null>(null);
	const at = useCallback((e: { clientX: number; clientY: number }, content: ReactNode) => setState({ x: e.clientX, y: e.clientY, content }), []);
	const under = useCallback((el: Element, content: ReactNode) => {
		const r = el.getBoundingClientRect();
		setState({ x: r.left + r.width / 2, y: r.bottom, content });
	}, []);
	const hide = useCallback(() => setState(null), []);
	return { state, at, under, hide };
}

/** The floating card. Render it once per chart, anywhere (it portals to <body>). */
export function TipLayer({ tip }: { tip: Tip }) {
	const s = tip.state;
	if (!s || typeof document === "undefined") return null;
	const vw = window.innerWidth;
	const vh = window.innerHeight;
	// ~16rem wide at most; flip left / up near the right / bottom edge.
	const left = s.x + 272 > vw ? Math.max(8, s.x - 264) : s.x + 12;
	const top = s.y + 140 > vh ? Math.max(8, s.y - 120) : s.y + 14;
	return createPortal(
		<div
			role="tooltip"
			className="pointer-events-none fixed z-[70] max-w-[16rem] rounded-btn border border-rule bg-sheet px-[0.75rem] py-[0.5rem] text-[0.8125rem] text-ink shadow-[0_0.5rem_1.5rem_-0.5rem_rgb(28_36_51/0.35)] print:hidden"
			style={{ left, top }}
		>
			{s.content}
		</div>,
		document.body,
	);
}

/** One line of a tooltip: a swatch, a label, a value at the right. */
export function TipRow({ color, label, value, strong = false }: { color?: string; label: ReactNode; value: ReactNode; strong?: boolean }) {
	return (
		<div className={`flex items-center gap-[0.5rem] ${strong ? "font-bold" : ""}`}>
			{color && <span aria-hidden="true" className="size-[0.625rem] shrink-0 rounded-[0.125rem]" style={{ background: color }} />}
			<span className="min-w-0 flex-1 text-muted">{label}</span>
			<span className="font-mono tabular-nums">{value}</span>
		</div>
	);
}
